import { Button, InputText, SelectCustom } from '@/components';
import { CustomTable } from '@/components/Table';
import { toCurrency } from '@/helpers/masks/toCurrency';
import { FormData, FormErrors, OnChangeFormInput } from '@/hooks';
import { EventPosReport } from '@/model/EventPosReport';
import React, { Fragment } from 'react';
import { Card } from 'reactstrap';
import Search from '@/assets/images/svg/search.svg';
import { FormInputFilter } from '../types';

interface StateProps {
  eventPosReport?: EventPosReport;
  formData: FormData;
  formErrors: FormErrors;
}

interface DispatchProps {
  onChangeFormInput: OnChangeFormInput;
  onFilter: () => void;
  onClearFilter: () => void;
  generatePDF: () => void;
}

type Props = StateProps & DispatchProps;

export const SalePosContent: React.FC<Props> = ({
  eventPosReport,
  formData,
  formErrors,
  onChangeFormInput,
  onFilter,
  onClearFilter,
  generatePDF,
}): JSX.Element => (
  <Fragment>
    <div className="d-flex justify-content-between">
      <h5>Vendas por POS</h5>
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', justifyContent: 'center' }}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: '15px', justifyContent: 'center' }}
        >
          <SelectCustom
            name="date"
            label="Data"
            placeholder=""
            className="w-input-sm"
            value={formData[FormInputFilter.date]}
            onChange={e => onChangeFormInput(FormInputFilter.date)(e?.value as string)}
            error={
              formErrors.ticketPhysicalSaleInstallments &&
              formErrors.ticketPhysicalSaleInstallments[0]
            }
            options={
              eventPosReport && eventPosReport.dates && eventPosReport.dates.length > 0
                ? eventPosReport.dates.map(date => ({ label: date, value: date }))
                : []
            }
          />
          <InputText
            name="serialNumber"
            label="POS:"
            placeholder="11111111111"
            value={formData[FormInputFilter.serialNumber]}
            onChange={e => onChangeFormInput(FormInputFilter.serialNumber)(e.target.value)}
            error={formErrors.serialNumber && formErrors.serialNumber[0]}
          />
        </div>
        <div
          style={{ justifyContent: 'center', display: 'flex', alignItems: 'center', gap: '15px' }}
        >
          <img
            src={Search}
            alt="Buscar"
            style={{
              cursor: 'pointer',
              padding: '10px',
              border: '1px solid #ccc',
              borderRadius: '5px',
              backgroundColor: '#f0f0f0',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              width: '55px',
              height: '55px',
              marginBottom: '20px',
            }}
            onClick={onFilter}
          />
          <div
            style={{
              cursor: 'pointer',
              padding: '10px',
              border: '1px solid #ccc',
              borderRadius: '5px',
              backgroundColor: '#ae0a0a',
              boxShadow: '0 2px 4px rgba(186, 4, 4, 0.1)',
              width: '55px',
              height: '55px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#ffffff',
            }}
            onClick={onClearFilter}
          >
            x
          </div>
        </div>
      </div>
      <div>
        <Button theme="outlineDark" onClick={() => generatePDF()} title="Exportar relatório" />
      </div>
    </div>
    <div>
      <h6 className="mb-4 collapseTableText">{`${
        eventPosReport && eventPosReport.machines && eventPosReport.machines.length > 0
          ? eventPosReport.machines.length
          : 0
      } ${
        eventPosReport && eventPosReport.machines && eventPosReport.machines.length > 1
          ? 'Maquininhas'
          : 'Maquininha'
      }`}</h6>
      <Card
        className="mb-5 card-no-border customTableScroll"
        style={{ border: 'none', overflow: 'auto' }}
      >
        <CustomTable
          theme="secondary"
          columns={[
            {
              name: 'Máquina',
              selector: row => row.serialNumber,
            },
            {
              name: 'Dinheiro',
              selector: row => row.value,
            },
            {
              name: 'Débito',
              selector: row => row.debit,
            },
            {
              name: 'Crédito',
              selector: row => row.credit,
            },
            {
              name: 'PIX',
              selector: row => row.pix,
            },
            {
              name: 'Taxa',
              selector: row => row.fee,
            },
            {
              name: 'Total Maquina',
              selector: row => row.total,
            },
          ]}
          data={
            eventPosReport && eventPosReport.machines && eventPosReport.machines.length > 0
              ? eventPosReport.machines.map(item => ({
                  serialNumber: item.serialNumber || '-----',
                  value: toCurrency(item.value) || 0,
                  debit: toCurrency(item.debit) || 0,
                  credit: toCurrency(item.credit) || 0,
                  pix: toCurrency(item.pix) || 0,
                  fee: toCurrency(item.fee) || 0,
                  total: toCurrency(item.total) || 0,
                }))
              : []
          }
          numberRowsPerPage={10}
          progressPending={false}
        />
      </Card>
    </div>

    <div>
      <h6 className="mb-4 collapseTableText">Totalizador Maquininhas</h6>
      <Card
        className="mb-5 card-no-border customTableScroll"
        style={{ border: 'none', overflow: 'auto' }}
      >
        <CustomTable
          theme="secondary"
          columns={[
            {
              name: 'Forma de Pagamento',
              selector: row => row.paymentType,
            },
            {
              name: 'Valor',
              selector: row => row.value,
            },
            {
              name: 'Taxas',
              selector: row => row.feeValue,
            },
            {
              name: 'Valor Total',
              selector: row => row.netValue,
            },
          ]}
          data={
            eventPosReport &&
            eventPosReport.machineSummary &&
            eventPosReport.machineSummary.length > 0
              ? eventPosReport.machineSummary.map(item => ({
                  paymentType: item.paymentType || '-----',
                  value: toCurrency(item.value) || 0,
                  feeValue: toCurrency(item.feeValue) || 0,
                  netValue: toCurrency(item.netValue) || 0,
                }))
              : []
          }
          numberRowsPerPage={10}
          progressPending={false}
        />
      </Card>
    </div>
  </Fragment>
);
